import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Tooltip,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Assessment as FinanceIcon,
  AttachMoney as MoneyIcon,
  ArrowBack as ArrowBackIcon,
  CloudUpload as UploadIcon,
  CheckCircle as ApproveIcon,
  Warning as PendingIcon,
  TrendingUp as IncomeIcon,
  TrendingDown as OutflowIcon,
  AccountBalance as BankIcon,
  Link as LinkIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import NumericField from './NumericField';

// Sample contracts data (in real app, this would come from Contracts module)
const availableContracts = [
  { id: 1, title: 'TechCorp Digital Transformation Contract', totalAmount: 62757.75, status: 'Active' },
  { id: 2, title: 'Global Manufacturing Analytics Contract', totalAmount: 50000, status: 'Signed' },
  { id: 3, title: 'European Consulting Platform Contract', totalAmount: 58398.25, status: 'Active' },
];

// Sample bank records data
const initialBankRecords = [
  {
    id: 1,
    bankRecordId: 'TXN-2024-001',
    date: '2024-01-15',
    amount: 18827.33,
    type: 'In',
    description: 'TechCorp Initial Payment',
    matchedContractId: 1,
    reconciliationStatus: 'Approved',
    note: 'Initial milestone payment received on time',
    isManual: false
  },
  {
    id: 2,
    bankRecordId: 'TXN-2024-002',
    date: '2024-01-20',
    amount: -5000,
    type: 'Out',
    description: 'Partner Payout - John Smith',
    matchedContractId: 1,
    reconciliationStatus: 'Approved',
    note: 'Team PM payout for TechCorp project',
    isManual: false
  },
  {
    id: 3,
    bankRecordId: 'TXN-2024-003',
    date: '2024-01-22',
    amount: -1500,
    type: 'Out',
    description: 'Vendor Payment - Software Licenses',
    matchedContractId: null,
    reconciliationStatus: 'Pending',
    note: 'Monthly software licenses for development tools',
    isManual: false
  },
  {
    id: 4,
    bankRecordId: 'TXN-2024-004',
    date: '2024-01-25',
    amount: 25000,
    type: 'In',
    description: 'Manufacturing Analytics Payment',
    matchedContractId: 2,
    reconciliationStatus: 'Pending',
    note: 'Contract signing payment',
    isManual: false
  },
  {
    id: 5,
    bankRecordId: 'MANUAL-001',
    date: '2024-01-28',
    amount: -850,
    type: 'Out',
    description: 'Office Supplies',
    matchedContractId: null,
    reconciliationStatus: 'Approved',
    note: 'Monthly office supplies purchase',
    isManual: true
  }
];

const reconciliationStatuses = ['Pending', 'Approved', 'Rejected', 'Review Required'];
const transactionTypes = ['In', 'Out'];

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`finance-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function Finance() {
  const navigate = useNavigate();
  const [bankRecords, setBankRecords] = useState(initialBankRecords);
  const [filteredRecords, setFilteredRecords] = useState(initialBankRecords);
  const [openDialog, setOpenDialog] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [csvData, setCsvData] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    contract: '',
    dateFrom: '',
    dateTo: ''
  });
  const [formData, setFormData] = useState({
    bankRecordId: '',
    date: '',
    amount: 0,
    type: 'In',
    description: '',
    matchedContractId: null,
    reconciliationStatus: 'Pending',
    note: ''
  });

  useEffect(() => {
    applyFilters();
  }, [filters, bankRecords]);

  const applyFilters = () => {
    let filtered = bankRecords;
    
    if (filters.type) {
      filtered = filtered.filter(record => record.type === filters.type);
    }
    if (filters.status) {
      filtered = filtered.filter(record => record.reconciliationStatus === filters.status);
    }
    if (filters.contract) {
      filtered = filtered.filter(record => record.matchedContractId === Number(filters.contract));
    }
    if (filters.dateFrom) {
      filtered = filtered.filter(record => record.date >= filters.dateFrom);
    }
    if (filters.dateTo) {
      filtered = filtered.filter(record => record.date <= filters.dateTo);
    }
    
    setFilteredRecords(filtered);
  };

  const calculateBankBalance = () => {
    return bankRecords.reduce((balance, record) => {
      return balance + (record.type === 'In' ? record.amount : -Math.abs(record.amount));
    }, 150000); // Starting balance
  };

  const getExpectedIncomeFromContracts = () => {
    // In real app, this would calculate from contract milestones
    return availableContracts
      .filter(contract => contract.status === 'Active' || contract.status === 'Signed')
      .reduce((sum, contract) => sum + contract.totalAmount, 0) * 0.7; // 70% expected remaining
  };

  const getExpectedOutflow = () => {
    // Calculate expected partner payouts and vendor costs
    const partnerPayouts = getExpectedIncomeFromContracts() * 0.3; // 30% for partners
    const vendorCosts = 15000; // Monthly vendor costs
    return partnerPayouts + vendorCosts;
  };

  const handleOpenDialog = (record = null) => {
    if (record) {
      setEditingRecord(record);
      setFormData({
        bankRecordId: record.bankRecordId,
        date: record.date,
        amount: Math.abs(record.amount),
        type: record.type,
        description: record.description,
        matchedContractId: record.matchedContractId,
        reconciliationStatus: record.reconciliationStatus,
        note: record.note
      });
    } else {
      setEditingRecord(null);
      setFormData({
        bankRecordId: '',
        date: new Date().toISOString().split('T')[0],
        amount: 0,
        type: 'In',
        description: '',
        matchedContractId: null,
        reconciliationStatus: 'Pending',
        note: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRecord(null);
  };

  const handleSubmit = () => {
    const recordData = {
      ...formData,
      amount: formData.type === 'Out' ? -Math.abs(formData.amount) : Math.abs(formData.amount),
      isManual: true,
      bankRecordId: formData.bankRecordId || `MANUAL-${Date.now()}`
    };

    if (editingRecord) {
      setBankRecords(bankRecords.map(record => 
        record.id === editingRecord.id ? { ...recordData, id: editingRecord.id } : record
      ));
    } else {
      const newRecord = { ...recordData, id: Date.now() };
      setBankRecords([...bankRecords, newRecord]);
    }
    handleCloseDialog();
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      setBankRecords(bankRecords.filter(record => record.id !== id));
    }
  };

  const handleReconciliationUpdate = (recordId, status, note = '') => {
    setBankRecords(bankRecords.map(record => 
      record.id === recordId 
        ? { ...record, reconciliationStatus: status, note: note || record.note }
        : record
    ));
  };

  const handleCsvUpload = () => {
    // Parse CSV data (simplified - in real app, use proper CSV parser)
    const lines = csvData.trim().split('\n');
    const headers = lines[0].split(',');
    const newRecords = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',');
      if (values.length >= 4) {
        newRecords.push({
          id: Date.now() + i,
          bankRecordId: values[0]?.trim() || `CSV-${Date.now()}-${i}`,
          date: values[1]?.trim() || new Date().toISOString().split('T')[0],
          amount: parseFloat(values[2]?.trim()) || 0,
          type: parseFloat(values[2]?.trim()) > 0 ? 'In' : 'Out',
          description: values[3]?.trim() || 'Imported transaction',
          matchedContractId: null,
          reconciliationStatus: 'Pending',
          note: 'Imported from CSV',
          isManual: false
        });
      }
    }

    setBankRecords([...bankRecords, ...newRecords]);
    setCsvData('');
    setOpenUploadDialog(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Pending': return 'warning';
      case 'Rejected': return 'error';
      case 'Review Required': return 'info';
      default: return 'default';
    }
  };

  const getTypeColor = (type) => {
    return type === 'In' ? 'success' : 'error';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getContractTitle = (contractId) => {
    const contract = availableContracts.find(c => c.id === contractId);
    return contract ? contract.title : 'No Contract';
  };

  const bankBalance = calculateBankBalance();
  const expectedIncome = getExpectedIncomeFromContracts();
  const expectedOutflow = getExpectedOutflow();
  const netProjection = expectedIncome - expectedOutflow;

  return (
    <Container maxWidth={false} sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0 }}>
     

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FinanceIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              Finance Management
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Bank reconciliation, contract matching, and cash flow management
            </Typography>
          </Box>
        </Box>
        <Box>
          <Button
            startIcon={<UploadIcon />}
            onClick={() => setOpenUploadDialog(true)}
            sx={{ mr: 1 }}
          >
            Upload CSV
          </Button>
          <Button
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ mr: 1 }}
          >
            Filters
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Record
          </Button>
        </Box>
      </Box>

      {/* Financial Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.main' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <BankIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                {formatCurrency(bankBalance)}
              </Typography>
              <Typography variant="body2" color="primary.dark">
                Current Bank Balance
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <IncomeIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {formatCurrency(expectedIncome)}
              </Typography>
              <Typography variant="body2" color="success.dark">
                Expected Income
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <OutflowIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" color="warning.main" fontWeight="bold">
                {formatCurrency(expectedOutflow)}
              </Typography>
              <Typography variant="body2" color="warning.dark">
                Expected Outflow
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            bgcolor: netProjection >= 0 ? 'success.50' : 'error.50', 
            border: '1px solid', 
            borderColor: netProjection >= 0 ? 'success.200' : 'error.200' 
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <MoneyIcon sx={{ fontSize: 32, color: netProjection >= 0 ? 'success.main' : 'error.main', mb: 1 }} />
              <Typography variant="h4" color={netProjection >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">
                {formatCurrency(netProjection)}
              </Typography>
              <Typography variant="body2" color={netProjection >= 0 ? 'success.dark' : 'error.dark'}>
                Net Projection
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      {showFilters && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.type}
                  label="Type"
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {transactionTypes.map(type => (
                    <MenuItem key={type} value={type}>{type === 'In' ? 'Income' : 'Outflow'}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {reconciliationStatuses.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Contract</InputLabel>
                <Select
                  value={filters.contract}
                  label="Contract"
                  onChange={(e) => setFilters({ ...filters, contract: e.target.value })}
                >
                  <MenuItem value="">All Contracts</MenuItem>
                  {availableContracts.map(contract => (
                    <MenuItem key={contract.id} value={contract.id}>{contract.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Bank Records Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Bank Record ID</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Matched Contract</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRecords.map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {record.isManual && <Chip label="Manual" size="small" sx={{ mr: 1 }} />}
                      <Typography variant="body2" fontFamily="monospace">
                        {record.bankRecordId}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{record.date}</TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight="bold" 
                      color={record.type === 'In' ? 'success.main' : 'error.main'}
                    >
                      {formatCurrency(Math.abs(record.amount))}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={record.type === 'In' ? 'Income' : 'Outflow'} 
                      color={getTypeColor(record.type)}
                      size="small"
                      icon={record.type === 'In' ? <IncomeIcon /> : <OutflowIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {record.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {record.matchedContractId ? (
                        <>
                          <LinkIcon sx={{ fontSize: 16, mr: 0.5, color: 'success.main' }} />
                          <Typography variant="caption">
                            {getContractTitle(record.matchedContractId)}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No match
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={record.reconciliationStatus}
                        onChange={(e) => handleReconciliationUpdate(record.id, e.target.value)}
                        variant="outlined"
                      >
                        {reconciliationStatuses.map(status => (
                          <MenuItem key={status} value={status}>{status}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(record)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(record.id)}
                        color="error"
                        disabled={!record.isManual}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Record Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRecord ? 'Edit Bank Record' : 'Add Bank Record'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bank Record ID"
                value={formData.bankRecordId}
                onChange={(e) => setFormData({ ...formData, bankRecordId: e.target.value })}
                placeholder="Auto-generated if empty"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumericField fullWidth label="Amount" value={formData.amount} onValueChange={(n)=>setFormData({ ...formData, amount: n ?? 0 })} mode="decimal" allowNegative={false} decimals={2} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Type"
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <MenuItem value="In">Income</MenuItem>
                  <MenuItem value="Out">Outflow</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Matched Contract</InputLabel>
                <Select
                  value={formData.matchedContractId || ''}
                  label="Matched Contract"
                  onChange={(e) => setFormData({ ...formData, matchedContractId: e.target.value || null })}
                >
                  <MenuItem value="">No Contract</MenuItem>
                  {availableContracts.map(contract => (
                    <MenuItem key={contract.id} value={contract.id}>{contract.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.reconciliationStatus}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, reconciliationStatus: e.target.value })}
                >
                  {reconciliationStatuses.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder="Reconciliation notes..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingRecord ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={openUploadDialog} onClose={() => setOpenUploadDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Upload Bank Records (CSV)</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            CSV Format: Bank Record ID, Date (YYYY-MM-DD), Amount, Description
            <br />
            Example: TXN-001, 2024-01-15, 1500.00, Payment received
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={10}
            label="CSV Data"
            value={csvData}
            onChange={(e) => setCsvData(e.target.value)}
            placeholder="Paste your CSV data here..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUploadDialog(false)}>Cancel</Button>
          <Button onClick={handleCsvUpload} variant="contained" disabled={!csvData.trim()}>
            Import Records
          </Button>
        </DialogActions>
      </Dialog>

      {/* Summary Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Records
              </Typography>
              <Typography variant="h4">
                {filteredRecords.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Pending Reconciliation
              </Typography>
              <Typography variant="h4">
                {filteredRecords.filter(r => r.reconciliationStatus === 'Pending').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Monthly Income
              </Typography>
              <Typography variant="h4">
                {formatCurrency(filteredRecords.filter(r => r.type === 'In').reduce((sum, r) => sum + Math.abs(r.amount), 0))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Monthly Outflow
              </Typography>
              <Typography variant="h4">
                {formatCurrency(filteredRecords.filter(r => r.type === 'Out').reduce((sum, r) => sum + Math.abs(r.amount), 0))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
} 